/**
 * EU legal-guarantee modal.
 *
 * Extends the theme's own ModalDialog (global.js), which already gives us Esc,
 * the focus trap, focus return, the scroll lock and the move to <body>. This
 * class only adds what the notice itself needs:
 *
 * - The notice files are 100-650 KB each and must not be fetched before the
 *   modal opens, so the src is parked in data-src and assigned on first open.
 * - Below the desktop breakpoint the graphic gets its own pan/zoom viewport, so
 *   magnifying the notice never zooms the modal or the page. Whether zooming
 *   applies is read from the viewport's computed touch-action rather than a
 *   breakpoint repeated here, so component-eu-guarantee.css stays the single
 *   definition of "mobile" for this feature.
 * - Triggers stay ordinary <a href="...#eu-guarantee"> menu items so the markup
 *   degrades to a real link and a deep link into the page opens the modal; the
 *   click is swallowed here to keep the hash off the URL. Keep SENTINEL in step
 *   with snippets/eu-guarantee.liquid, which decides server-side whether such a
 *   link renders at all.
 */
const SENTINEL = '#eu-guarantee';
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.5;

class EuGuarantee extends ModalDialog {
  constructor() {
    super();

    this.viewport = this.querySelector('[data-eu-guarantee-viewport]');
    this.graphic = this.querySelector('[data-eu-guarantee-graphic]');

    this.pointers = new Map();
    this.zoom = MIN_ZOOM;
    this.pinchDistance = 0;
    this.zoomable = false;

    this.syncZoomMode = this.syncZoomMode.bind(this);

    this.bindViewport();
    this.bindTriggers();

    this.querySelectorAll('[data-eu-guarantee-close]').forEach(button => {
      button.addEventListener('click', () => this.hide());
    });
  }

  connectedCallback() {
    // ModalDialog re-parents itself to <body>, so this fires twice.
    super.connectedCallback();

    if (this.initialised) return;
    this.initialised = true;

    this.syncZoomMode();
    window.addEventListener('resize', this.syncZoomMode, { passive: true });

    if (decodeURIComponent(window.location.hash) === SENTINEL) {
      this.show();
    }
  }

  show(opener) {
    this.hydrateGraphic();
    this.syncZoomMode();
    super.show(opener);
  }

  hide() {
    super.hide();
    this.resetZoom();
  }

  /** Menus are rendered before this element upgrades, so delegate from document. */
  bindTriggers() {
    document.addEventListener('click', event => {
      const trigger = event.target.closest('a[href]');

      if (!trigger || !trigger.getAttribute('href').includes(SENTINEL)) return;

      event.preventDefault();
      this.show(trigger);
    });

    document.querySelectorAll(`a[href*="${SENTINEL}"]`).forEach(trigger => {
      trigger.setAttribute('aria-haspopup', 'dialog');
    });
  }

  hydrateGraphic() {
    if (!this.graphic) return;

    const src = this.graphic.dataset.src;

    if (!src || this.graphic.getAttribute('src') === src) return;

    this.graphic.setAttribute('src', src);
  }

  syncZoomMode() {
    if (!this.viewport) return;

    const next = getComputedStyle(this.viewport).touchAction === 'none';

    if (next === this.zoomable) return;

    this.zoomable = next;

    if (!this.zoomable) this.resetZoom();
  }

  clamp(value) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
  }

  setZoom(next, focalX, focalY) {
    if (!this.viewport || !this.graphic) return;

    const target = this.clamp(next);

    if (target === this.zoom) return;

    const rect = this.viewport.getBoundingClientRect();
    const x = focalX === undefined ? rect.left + rect.width / 2 : focalX;
    const y = focalY === undefined ? rect.top + rect.height / 2 : focalY;
    const offsetX = x - rect.left;
    const offsetY = y - rect.top;
    const ratio = target / this.zoom;

    this.zoom = target;
    this.graphic.style.width = `${this.zoom * 100}%`;

    this.viewport.scrollLeft = (this.viewport.scrollLeft + offsetX) * ratio - offsetX;
    this.viewport.scrollTop = (this.viewport.scrollTop + offsetY) * ratio - offsetY;
  }

  resetZoom() {
    if (!this.viewport || !this.graphic) return;

    this.zoom = MIN_ZOOM;
    this.graphic.style.width = '100%';
    this.viewport.scrollTo(0, 0);
  }

  distanceBetweenPointers() {
    const [a, b] = Array.from(this.pointers.values());

    if (!a || !b) return 0;

    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  midpointOfPointers() {
    const [a, b] = Array.from(this.pointers.values());

    if (!a || !b) return null;

    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  bindViewport() {
    if (!this.viewport) return;

    const viewport = this.viewport;

    viewport.addEventListener('pointerdown', event => {
      if (!this.zoomable) return;

      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      viewport.setPointerCapture(event.pointerId);

      if (this.pointers.size === 2) {
        this.pinchDistance = this.distanceBetweenPointers();
      }
    });

    viewport.addEventListener('pointermove', event => {
      const previous = this.pointers.get(event.pointerId);

      if (!previous) return;

      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size >= 2) {
        const distance = this.distanceBetweenPointers();
        const midpoint = this.midpointOfPointers();

        if (this.pinchDistance > 0 && distance > 0 && midpoint) {
          this.setZoom(this.zoom * (distance / this.pinchDistance), midpoint.x, midpoint.y);
        }

        this.pinchDistance = distance;
        return;
      }

      viewport.scrollLeft -= event.clientX - previous.x;
      viewport.scrollTop -= event.clientY - previous.y;
    });

    const releasePointer = event => {
      this.pointers.delete(event.pointerId);

      if (this.pointers.size < 2) this.pinchDistance = 0;
    };

    viewport.addEventListener('pointerup', releasePointer);
    viewport.addEventListener('pointercancel', releasePointer);

    viewport.addEventListener(
      'wheel',
      event => {
        if (!this.zoomable) return;

        event.preventDefault();

        if (event.ctrlKey) {
          this.setZoom(
            this.zoom * (event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP),
            event.clientX,
            event.clientY
          );
          return;
        }

        viewport.scrollTop += event.deltaY;
        viewport.scrollLeft += event.deltaX;
      },
      { passive: false }
    );

    viewport.addEventListener('dblclick', event => {
      if (!this.zoomable) return;

      if (this.zoom > MIN_ZOOM) {
        this.resetZoom();
        return;
      }

      this.setZoom(this.zoom * ZOOM_STEP * 2, event.clientX, event.clientY);
    });

    viewport.addEventListener('keydown', event => {
      if (!this.zoomable) return;

      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.setZoom(this.zoom * ZOOM_STEP);
      } else if (event.key === '-') {
        event.preventDefault();
        this.setZoom(this.zoom / ZOOM_STEP);
      } else if (event.key === '0') {
        event.preventDefault();
        this.resetZoom();
      }
    });

    this.addEventListener('click', event => {
      const button = event.target.closest('[data-eu-guarantee-zoom]');

      if (!this.zoomable || !button) return;

      const action = button.dataset.euGuaranteeZoom;

      if (action === 'in') this.setZoom(this.zoom * ZOOM_STEP);
      else if (action === 'out') this.setZoom(this.zoom / ZOOM_STEP);
      else this.resetZoom();
    });
  }
}

customElements.define('eu-guarantee', EuGuarantee);
